'use client';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, RotateCcw, Trophy, Clock, Lightbulb } from 'lucide-react';

interface Props { onBack: () => void; }

type Grid = (number | null)[][];
type FixedGrid = boolean[][];

const LEADERBOARD = [
  { name: 'Priya S.', time: '4:12', difficulty: 'Hard', score: 9200 },
  { name: 'Arjun K.', time: '5:38', difficulty: 'Hard', score: 8100 },
  { name: 'Yuki H.', time: '3:05', difficulty: 'Medium', score: 7500 },
  { name: 'Marcus T.', time: '6:44', difficulty: 'Hard', score: 6800 },
  { name: 'Sofia R.', time: '2:50', difficulty: 'Easy', score: 5500 },
];

// Solved puzzle base
const SOLVED: Grid = [
  [5,3,4,6,7,8,9,1,2],
  [6,7,2,1,9,5,3,4,8],
  [1,9,8,3,4,2,5,6,7],
  [8,5,9,7,6,1,4,2,3],
  [4,2,6,8,5,3,7,9,1],
  [7,1,3,9,2,4,8,5,6],
  [9,6,1,5,3,7,2,8,4],
  [2,8,7,4,1,9,6,3,5],
  [3,4,5,2,8,6,1,7,9],
];

function createPuzzle(difficulty: 'easy' | 'medium' | 'hard'): { puzzle: Grid; fixed: FixedGrid } {
  const removals = difficulty === 'easy' ? 30 : difficulty === 'medium' ? 45 : 55;
  const puzzle: Grid = SOLVED.map(row => [...row]);
  const fixed: FixedGrid = Array(9).fill(null).map(() => Array(9).fill(true));
  const positions = Array.from({ length: 81 }, (_, i) => [Math.floor(i / 9), i % 9] as [number, number]);
  const shuffled = positions.sort(() => Math.random() - 0.5);
  let removed = 0;
  for (const [r, c] of shuffled) {
    if (removed >= removals) break;
    puzzle[r][c] = null;
    fixed[r][c] = false;
    removed++;
  }
  return { puzzle, fixed };
}

function isValidPlacement(grid: Grid, row: number, col: number, val: number): boolean {
  for (let i = 0; i < 9; i++) {
    if (i !== col && grid[row][i] === val) return false;
    if (i !== row && grid[i][col] === val) return false;
  }
  const br = Math.floor(row / 3) * 3;
  const bc = Math.floor(col / 3) * 3;
  for (let r = br; r < br + 3; r++) {
    for (let c = bc; c < bc + 3; c++) {
      if ((r !== row || c !== col) && grid[r][c] === val) return false;
    }
  }
  return true;
}

function isComplete(grid: Grid): boolean {
  return grid.every(row => row.every(cell => cell !== null));
}

export default function SudokuGame({ onBack }: Props) {
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [{ puzzle: grid, fixed }, setGameState] = useState(() => createPuzzle('medium'));
  const [selected, setSelected] = useState<[number, number] | null>(null);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [elapsed, setElapsed] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [xp, setXp] = useState(0);
  const [hints, setHints] = useState(3);
  const [errorCount, setErrorCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (gameStarted && !gameOver) {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [gameStarted, gameOver]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const startNewGame = useCallback((diff: 'easy' | 'medium' | 'hard') => {
    if (timerRef.current) clearInterval(timerRef.current);
    setDifficulty(diff);
    setGameState(createPuzzle(diff));
    setSelected(null);
    setErrors(new Set());
    setElapsed(0);
    setGameStarted(false);
    setGameOver(false);
    setHints(3);
    setErrorCount(0);
  }, []);

  const handleCellClick = (r: number, c: number) => {
    if (!fixed[r][c]) setSelected([r, c]);
  };

  const handleNumberInput = useCallback((num: number) => {
    if (!selected || gameOver) return;
    const [r, c] = selected;
    if (fixed[r][c]) return;
    if (!gameStarted) setGameStarted(true);

    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = num;
    const key = `${r}-${c}`;
    const newErrors = new Set(errors);

    if (!isValidPlacement(newGrid, r, c, num)) {
      newErrors.add(key);
      setErrorCount(e => e + 1);
    } else {
      newErrors.delete(key);
    }
    setErrors(newErrors);
    setGameState(prev => ({ puzzle: newGrid, fixed: prev.fixed }));

    if (isComplete(newGrid) && newErrors.size === 0) {
      setGameOver(true);
      if (timerRef.current) clearInterval(timerRef.current);
      const score = Math.max(100, 10000 - elapsed * 5 - errorCount * 100);
      setXp(prev => prev + score);
    }
  }, [selected, grid, fixed, errors, gameOver, gameStarted, elapsed, errorCount]);

  const handleErase = useCallback(() => {
    if (!selected || gameOver) return;
    const [r, c] = selected;
    if (fixed[r][c]) return;
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = null;
    const newErrors = new Set(errors);
    newErrors.delete(`${r}-${c}`);
    setErrors(newErrors);
    setGameState(prev => ({ puzzle: newGrid, fixed: prev.fixed }));
  }, [selected, grid, fixed, errors, gameOver]);

  const handleHint = useCallback(() => {
    if (hints <= 0 || !selected || gameOver) return;
    const [r, c] = selected;
    if (fixed[r][c] || grid[r][c] === SOLVED[r][c]) return;
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = SOLVED[r][c];
    const newErrors = new Set(errors);
    newErrors.delete(`${r}-${c}`);
    setErrors(newErrors);
    setHints(h => h - 1);
    setGameState(prev => ({ puzzle: newGrid, fixed: prev.fixed }));
  }, [hints, selected, grid, fixed, errors, gameOver]);

  const isHighlighted = (r: number, c: number) => {
    if (!selected) return false;
    const [sr, sc] = selected;
    return r === sr || c === sc || (Math.floor(r / 3) === Math.floor(sr / 3) && Math.floor(c / 3) === Math.floor(sc / 3));
  };

  const isSameNumber = (r: number, c: number) => {
    if (!selected) return false;
    const selVal = grid[selected[0]][selected[1]];
    return selVal !== null && grid[r][c] === selVal;
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="btn-ghost p-2 rounded-lg"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">🔢 Sudoku</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Fill every row, column and 3×3 box with 1–9</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Difficulty */}
          <div className="flex gap-2 mb-4">
            {(['easy', 'medium', 'hard'] as const).map((d) => (
              <button key={d} onClick={() => startNewGame(d)}
                className="px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-all"
                style={{
                  background: difficulty === d ? '#10B981' : 'var(--card)',
                  color: difficulty === d ? '#fff' : 'var(--muted-foreground)',
                  border: `1px solid ${difficulty === d ? '#10B981' : 'var(--border)'}`,
                }}>
                {d}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Time', value: formatTime(elapsed), color: '#06B6D4' },
              { label: 'Errors', value: errorCount, color: '#F43F5E' },
              { label: 'Hints', value: hints, color: '#F59E0B' },
              { label: 'XP', value: xp, color: '#10B981' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Game over */}
          {gameOver && (
            <div className="rounded-xl p-4 mb-5 text-center"
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)' }}>
              <p className="text-xl font-bold" style={{ color: '#10B981' }}>🎉 Puzzle Solved!</p>
              <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                {formatTime(elapsed)} · {errorCount} errors · +{Math.max(100, 10000 - elapsed * 5 - errorCount * 100)} XP
              </p>
              <button onClick={() => startNewGame(difficulty)} className="btn-accent mt-3 text-sm">New Puzzle</button>
            </div>
          )}

          {/* Sudoku grid */}
          <div className="flex justify-center mb-5">
            <div className="rounded-xl overflow-hidden" style={{ border: '3px solid #10B981' }}>
              {grid.map((row, rIdx) => (
                <div key={rIdx} className="flex" style={{ borderBottom: rIdx === 2 || rIdx === 5 ? '3px solid #10B981' : '1px solid var(--border)' }}>
                  {row.map((cell, cIdx) => {
                    const isFixed = fixed[rIdx][cIdx];
                    const isSel = selected?.[0] === rIdx && selected?.[1] === cIdx;
                    const isHL = isHighlighted(rIdx, cIdx);
                    const isSameNum = isSameNumber(rIdx, cIdx);
                    const isError = errors.has(`${rIdx}-${cIdx}`);
                    return (
                      <button
                        key={cIdx}
                        onClick={() => handleCellClick(rIdx, cIdx)}
                        className="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-base sm:text-lg font-bold transition-all duration-100"
                        style={{
                          background: isSel
                            ? 'rgba(16,185,129,0.4)'
                            : isError
                            ? 'rgba(244,63,94,0.25)'
                            : isSameNum
                            ? 'rgba(16,185,129,0.15)'
                            : isHL
                            ? 'rgba(16,185,129,0.07)'
                            : 'var(--card)',
                          color: isError ? '#F43F5E' : isFixed ? '#F1F5F9' : '#10B981',
                          borderRight: cIdx === 2 || cIdx === 5 ? '3px solid #10B981' : '1px solid var(--border)',
                          fontWeight: isFixed ? 700 : 500,
                          cursor: isFixed ? 'default' : 'pointer',
                        }}
                      >
                        {cell ?? ''}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Number pad */}
          <div className="flex justify-center gap-2 mb-3">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} onClick={() => handleNumberInput(n)}
                className="w-10 h-10 rounded-xl font-bold text-base transition-all hover:scale-105"
                style={{ background: 'var(--card)', border: '1px solid var(--border)', color: '#10B981' }}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-3">
            <button onClick={handleErase} className="btn-ghost text-sm flex items-center gap-2">
              <RotateCcw size={14} /> Erase
            </button>
            <button onClick={handleHint} disabled={hints === 0}
              className="btn-ghost text-sm flex items-center gap-2"
              style={{ opacity: hints === 0 ? 0.5 : 1 }}>
              <Lightbulb size={14} /> Hint ({hints})
            </button>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <Trophy size={16} style={{ color: '#F59E0B' }} /> Best Solvers
          </h3>
          <div className="space-y-3">
            {LEADERBOARD.map((entry, i) => (
              <div key={entry.name} className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: i === 0 ? 'rgba(245,158,11,0.1)' : 'var(--muted)', border: `1px solid ${i === 0 ? 'rgba(245,158,11,0.3)' : 'transparent'}` }}>
                <span className="text-base font-bold w-6 text-center"
                  style={{ color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#CD7F32' : 'var(--muted-foreground)' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{entry.name}</p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <Clock size={10} className="inline mr-1" />{entry.time} · {entry.difficulty}
                  </p>
                </div>
                <span className="text-sm font-bold" style={{ color: '#10B981' }}>{entry.score.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-xl text-center" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Session XP</p>
            <p className="text-xl font-bold" style={{ color: '#10B981' }}>{xp.toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
